import {
  useCallback, useEffect, useState
} from 'react'
import {
  Sparkles
} from 'lucide-react'
import {
  integrationSuggestionsService,
  isInlineConnectable,
} from '../services/api'
import type {
  IntegrationSuggestionItem
} from '../services/api'
import {
  SuggestedIntegrationTile
} from './SuggestedIntegrationTile'
import {
  useSuggestionConnect, verdictFromShortlist
} from '../hooks/useSuggestionConnect'
import {
  OnboardingEvents,
  trackEvent
} from '../analytics/posthog'
import type {
  IntegrationSuggestionSurface
} from '../analytics/posthog'

// Six suggestions plus the Other tile is seven cells — one line across the full
// width, which is what makes these tiles come out the same size as the
// three-across ones in the half-width category cards below.
const ROW_LIMIT = 6

interface SuggestedIntegrationsCardProps {
  // Which surface this mount is, for the dismissal event. Taken from the
  // parent rather than asserted here: the onboarding step renders these same
  // tiles by hand today, and if the two are ever consolidated onto this card a
  // hard-coded label would emit `integrations_page` from onboarding — the
  // exact mislabelling the property exists to prevent, with nothing to catch
  // it. A prop makes each mount name itself.
  surface: IntegrationSuggestionSurface
  // Opens the full app catalog. The category rows' own OtherTile opens the
  // "request an integration" form instead; here that is the wrong first answer,
  // because thousands of apps are already connectable and the browse modal's
  // empty state is the natural place to fall through to a request.
  //
  // Optional because the catalog is not always on screen — it is behind
  // `FeatureFlags.PIPEDREAM_CATALOG_BROWSE`. Omitted, the browse tile is not
  // rendered at all rather than rendered inert: this callback's only job is to
  // open a card that isn't there, and a tile that looks clickable and does
  // nothing is the exact regression this row shipped once already.
  onBrowseAll?: (() => void) | undefined
  // Fallback for suggestions that cannot be connected inline — discovered-OAuth
  // apps, which need a resource URL only the user has. The parent opens the
  // catalog so they can complete it there.
  //
  // Optional for the same reason, and with the same degradation the onboarding
  // step already uses: with no catalog to fall through to, a non-inline
  // suggestion renders as a preview with no click, so it still tells the user
  // the app was spotted without promising an action that cannot complete.
  //
  // Everything else now connects through the shared dispatcher below rather
  // than through the parent. That is the fix for the gap this prop used to
  // paper over: routing every suggestion into the Pipedream catalog wrote a
  // `PipedreamAppConnection` even for in-house apps, so connecting Notion from
  // a suggestion produced a different (and shallower) integration than
  // connecting Notion from its own tile six inches away.
  onManualConnect?: ((suggestion: IntegrationSuggestionItem) => void) | undefined
  // Requests that the page refresh its separate connected-app list after this
  // row confirms a connection or cannot verify the result. The latter is
  // intentionally included: another idempotent read is cheaper than leaving a
  // successful desktop popup connect invisible because the shortlist read failed.
  onConnectionsRefreshRequested?: (() => void) | undefined
}

/**
 * "More tools you might need" — the durable home of the inbox scan's shortlist.
 *
 * Hides itself entirely when there is nothing to suggest. An empty
 * "More tools you might need" is worse than no row at all, and after a user
 * connects or dismisses their way through the list that is the steady state.
 */
export function SuggestedIntegrationsCard({
  surface,
  onBrowseAll,
  onManualConnect,
  onConnectionsRefreshRequested,
}: SuggestedIntegrationsCardProps): JSX.Element | null {
  const [suggestions, setSuggestions] = useState<IntegrationSuggestionItem[]>([])
  const [dismissing, setDismissing] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)

  // Returns the keys still being suggested, so a caller can tell whether one
  // particular tile survived the read — this surface's only definition of
  // connected. `null` is "the read failed", deliberately distinct from an empty
  // set, and `verdictFromShortlist` is what turns either into an answer.
  const refresh = useCallback(async (): Promise<Set<string> | null> => {
    try {
      const result = await integrationSuggestionsService.list(ROW_LIMIT, { excludeBuiltIn: true })
      setSuggestions(result.suggestions)
      return new Set(result.suggestions.map((s) => s.catalog_key))
    } catch {
      // Keep the row as it stands; the next mount re-reads anyway.
      return null
    }
  }, [])

  // Unlike onboarding, a connected app simply leaves this row — the server
  // stops suggesting what is already connected, and its real tile is elsewhere
  // on this same page, showing connected state with a disconnect affordance.
  // Marking it connected here would duplicate that a few pixels away.
  const { connectingKey, error, connect } = useSuggestionConnect({
    surface,
    onSettled: async (suggestion) => {
      const verdict = verdictFromShortlist(
        await refresh(),
        suggestion.catalog_key,
      )
      if (verdict !== 'not_connected') onConnectionsRefreshRequested?.()
      return verdict
    },
  })

  useEffect(() => {
    let cancelled = false
    void integrationSuggestionsService
      .list(ROW_LIMIT, { excludeBuiltIn: true })
      .then((result) => {
        if (cancelled) return
        setSuggestions(result.suggestions)
        setLoaded(true)
      })
      // A failed read hides the row. It is an enhancement to a page that works
      // without it, so it must never render an error where a suggestion goes.
      .catch(() => {
        if (!cancelled) setLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleDismiss = useCallback(
    async (suggestion: IntegrationSuggestionItem) => {
      setDismissing(suggestion.catalog_key)
      trackEvent(OnboardingEvents.INTEGRATION_SUGGESTION_DISMISSED, {
        catalog_key: suggestion.catalog_key,
        surface,
      })
      try {
        await integrationSuggestionsService.dismiss(suggestion.catalog_key)
        // Refetch rather than splice: the seventh suggestion should rotate up
        // into the freed slot, and only the server knows what that is.
        await refresh()
      } catch {
        // Leave the tile in place — a dismissal that silently didn't persist
        // would reappear on the next load anyway, so pretending it worked is
        // the more confusing outcome.
      } finally {
        setDismissing(null)
      }
    },
    [surface, refresh],
  )

  if (!loaded || suggestions.length === 0) return null

  return (
    <div className="md:col-span-2 bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-primary-500" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">More tools you might need</h3>
          <p className="text-xs text-gray-500">Suggested for you</p>
        </div>
      </div>
      {error !== null && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2 rounded">
          {error}
        </div>
      )}
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {suggestions.map((suggestion) => {
          // A suggestion is actionable if it can start inline, or if there is a
          // catalog to hand the awkward ones off to. Decided here rather than
          // inside the handler so an unactionable tile carries no `onClick` at
          // all — it renders as a preview instead of a button that swallows the
          // tap (and would still have fired the connect-clicked event, quietly
          // inflating the one metric that says whether these tiles work).
          const inline = isInlineConnectable(suggestion)
          const actionable = inline || onManualConnect !== undefined
          return (
            <SuggestedIntegrationTile
              key={suggestion.catalog_key}
              suggestion={suggestion}
              loading={
                dismissing === suggestion.catalog_key
                || connectingKey === suggestion.catalog_key
              }
              // This row mounts ON the Settings page, so the tile's default
              // inert hint ("connect in Settings") would name the page the user
              // is already on — and with no catalog there is nothing on it for
              // them. Say the state instead, and say why: the card's own
              // subtitle already reads "Suggested for you", so repeating only
              // that tells a screen reader nothing it hasn't heard.
              //
              // A stale-row guard, not a common path: this row fetches with
              // `excludeBuiltIn`, so IN_HOUSE and PROMOTED never reach it —
              // between them the tiers holding Zoho (the only app answering
              // MANUAL *by design*) and any retired promoted app (MANUAL by
              // `_promoted_connect_method`'s fallback). A Pipedream row is
              // either inline-connectable or dropped from the query entirely.
              // What is left is a row outliving its catalog entry, which
              // `_connect_method` answers MANUAL for.
              {...(actionable
                ? {}
                : { inertHint: 'suggested for you, no setup available here' })}
              onClick={actionable
                ? () => {
                  if (inline) {
                    // The hook counts this tap, because it is the only place
                    // that can tell an accepted one from a tap its in-flight
                    // guard refuses (GET-13089).
                    void connect(suggestion)
                    return
                  }
                  // The manual route never reaches the hook, so it counts its
                  // own click. It has no terminal event either way — the flow
                  // it hands off to is not part of this funnel.
                  trackEvent(OnboardingEvents.INTEGRATION_SUGGESTION_CONNECT_CLICKED, {
                    catalog_key: suggestion.catalog_key,
                    surface,
                  })
                  onManualConnect?.(suggestion)
                }
                : undefined}
              onDismiss={() => void handleDismiss(suggestion)}
            />
          )
        })}
        {onBrowseAll !== undefined && (
          <button
            type="button"
            onClick={onBrowseAll}
            className="w-full flex flex-col items-center justify-center gap-2 p-4 bg-gray-50 border border-dashed border-gray-300 rounded-xl hover:border-primary-300 hover:bg-primary-50 transition-colors cursor-pointer"
          >
            <svg
              className="w-8 h-8 text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <circle cx="12" cy="12" r="10" strokeWidth="1.5" />
              <line x1="12" y1="8" x2="12" y2="16" strokeWidth="1.5" />
              <line x1="8" y1="12" x2="16" y2="12" strokeWidth="1.5" />
            </svg>
            <span className="text-xs font-medium text-gray-400">Other</span>
          </button>
        )}
      </div>
    </div>
  )
}
